/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { FormContainer } from '@apps-common/apps/portal/pages/authenticate/components/FormContainer'
import { ButtonRoundedSm, InputSM } from '@apps-common/components/elements'
import { VertSpacerMd } from '@apps-common/components/layout'
import { AUTHENTICATE_OPTION_LOGIN } from '@apps-common/config/authenticate-options'
import { commonIcons } from '@apps-common/config/icons'
import { useAuth } from '@apps-common/contexts'
import { useAuthenticateScreenStore } from '@apps-common/stores/useAuthenticateScreenStore'
import { useIonRouter } from '@ionic/react'

type FormInputs = {
  username: string
}
interface IProps {
  isMobile: boolean
}

export const ForgotPasswordByUsernameForm: React.FC<IProps> = ({ isMobile }) => {
  const router = useIonRouter()

  const { setScreen } = useAuthenticateScreenStore()
  const { forgotPasswordByUsername } = useAuth()
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState<string | null>(null)
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormInputs>()

  const onSubmit = async (inputs: FormInputs) => {
    setForgotPasswordSuccess(null)
    setForgotPasswordError(null)

    const res = await forgotPasswordByUsername(inputs.username)

    if (!res.result) {
      setForgotPasswordError(res.message || 'Failed to send password reset')
    } else {
      reset()
      setForgotPasswordSuccess(res.message || 'Password reset link sent successfully')
    }
  }

  return (
    <FormContainer isMobile={isMobile} className='max-w-[450px] md:min-w-[450px]'>
      <div className='absolute top-0 left-0 text-on-mm-brand bg-gradient-to-br from-mm-brand to-mm-brand-2 w-full flex items-center justify-center p-2'>
        <div className='flex items-center justify-center'>
          <h2 className='text-[1rem] jost-medium'>Forgot Password</h2>
        </div>
      </div>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        {forgotPasswordSuccess && (
          <div className='bg-success px-3 py-3 rounded-2xl mb-[20px]'>
            <p className='text-center text-[0.9em] text-on-success'>{forgotPasswordSuccess}</p>
          </div>
        )}
        {forgotPasswordError && (
          <div className='bg-error px-2 py-1 rounded-2xl mb-[20px]'>
            <p className='text-center text-[0.9em] text-on-error'>{forgotPasswordError}</p>
          </div>
        )}
        <div className='my-4 flex flex-col'>
          <InputSM
            type='username'
            placeholder='Enter your username'
            label='Username'
            showLabel
            className=''
            id='forgot-username'
            registration={register('username', {
              validate: (value) => {
                if (!value || value.trim() === '') {
                  return 'Required'
                }

                return true
              }
            })}
            disabled={isSubmitting}
            errors={errors.username}
          />
          <div className='flex items-center justify-between'>
            <button
              type='button'
              className='text-text/70 hover:opacity-80 disabled:opacity-70 disabled:cursor-not-allowed text-[0.8rem] jost-medium flex items-center justify-end'
              disabled={isSubmitting}
              onClick={(e) => {
                e?.stopPropagation()
                setScreen(AUTHENTICATE_OPTION_LOGIN)
                router.push('/authenticate', 'root', 'replace')
              }}
            >
              <commonIcons.arrowRightShort className='rotate-180 text-[1.2em]' /> Back to login
            </button>
          </div>
        </div>
        <VertSpacerMd />
        <ButtonRoundedSm
          type='submit'
          loading={isSubmitting}
          text='Send link'
          icon={<commonIcons.arrowAngled />}
          iconRight
          className='w-full text-on-mm-brand-2 bg-gradient-to-r from-mm-brand to-mm-brand-2'
        />
      </form>
    </FormContainer>
  )
}
